import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { zoneMasterAPI, masterDataAPI } from "../services/api";
import Swal from "sweetalert2";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./RaiseComplaint.css";
import "./AddEditZoneMaster.css";
import { getRoutePermissions } from "../utils/auth";

const normalizeCountry = (item) => ({
  countryCode: item.countryCode || item.CountryCode || item.COUNTRYCODE || "",
  countryName: item.countryName || item.CountryName || item.COUNTRYNAME || "",
});

const normalizeRegion = (item) => ({
  regionCode: item.regionCode || item.RegionCode || item.REGIONCODE || "",
  regionName: item.regionName || item.RegionName || item.REGIONNAME || "",
  countryCode: item.countryCode || item.CountryCode || item.COUNTRYCODE || "",
});

const normalizeZone = (item) => ({
  zoneCode: item.zoneCode || item.ZoneCode || item.ZONECODE || "",
  zoneId: item.zoneId || item.ZoneId || item.ZONEID || "",
  zoneName: item.zoneName || item.ZoneName || item.ZONENAME || "",
  countryCode: item.countryCode || item.CountryCode || item.COUNTRYCODE || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

const AddEditZoneMaster = () => {
  const navigate = useNavigate();
  const { zoneCode } = useParams();
  const isEditMode = !!zoneCode;

  const routePermissions = getRoutePermissions("/zones");
  const { canAdd, canEdit } = routePermissions;
  const canSave = isEditMode ? canEdit : canAdd;

  const [zone, setZone] = useState({
    zoneCode: "",
    zoneId: "",
    zoneName: "",
    countryCode: "",
    isActive: true,
  });

  const [dropdowns, setDropdowns] = useState({
    countries: [],
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const countrySelectRef = useRef(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    if (!canSave) {
      Swal.fire({
        title: "Access Denied",
        text: `You do not have permission to ${isEditMode ? "edit" : "add"} zones.`,
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => navigate("/zones"));
      return;
    }
    loadDropdowns();
    if (isEditMode) {
      loadZone();
    } else {
      loadNextZoneCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneCode, canSave]);

  // Initialize Select2 for Country dropdown
  useEffect(() => {
    if (dropdowns.countries.length === 0) {
      return;
    }

    if (countrySelectRef.current) {
      const $countrySelect = $(countrySelectRef.current);
      if ($countrySelect.data("select2")) {
        $countrySelect.select2("destroy");
      }
      $countrySelect.select2({
        width: "100%",
        placeholder: "Select Country",
        allowClear: false,
      });
      $countrySelect.on("select2:select", function (e) {
        const selectedCountryCode = e.params.data.id;
        handleChange("countryCode", selectedCountryCode);
      });
      if (zone.countryCode) {
        $countrySelect.val(zone.countryCode).trigger("change");
      }
    }

    return () => {
      if (countrySelectRef.current) {
        const $select = $(countrySelectRef.current);
        if ($select.data("select2")) {
          $select.off("select2:select");
          $select.select2("destroy");
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdowns.countries]);

  // Update Select2 value when zone changes
  useEffect(() => {
    if (countrySelectRef.current && dropdowns.countries.length > 0) {
      const $countrySelect = $(countrySelectRef.current);
      if ($countrySelect.data("select2")) {
        const currentVal = $countrySelect.val();
        if (currentVal !== (zone.countryCode || "")) {
          $countrySelect.val(zone.countryCode || "").trigger("change");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zone.countryCode]);

  const loadDropdowns = async () => {
    try {
      setLoading(true);
      const countryRes = await masterDataAPI.getCountries();

      setDropdowns({
        countries: (countryRes?.data || []).map((c) => normalizeCountry(c)),
      });
    } catch (e) {
      console.error("Error loading dropdowns:", e);
      setError("Failed to load dropdown data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };


  const loadNextZoneCode = async () => {
    try {
      const response = await zoneMasterAPI.getNextCode();
      if (response && response.success && response.data) {
        setZone((prev) => ({ ...prev, zoneCode: response.data }));
      }
    } catch (e) {
      console.error("Error loading next zone code:", e);
      setZone((prev) => ({ ...prev, zoneCode: "ZN00001" }));
    }
  };

  const loadZone = async () => {
    try {
      setLoading(true);
      const response = await zoneMasterAPI.getByCode(zoneCode);
      if (response && response.success && response.data) {
        const zoneData = normalizeZone(response.data);
        setZone(zoneData);
      } else {
        navigate("/zones");
      }
    } catch (e) {
      console.error("Error loading zone:", e);
      if (e.response?.status !== 404) {
        Swal.fire("Error", "Failed to load zone details", "error");
      }
      navigate("/zones");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setZone((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!zone.zoneCode?.trim()) {
      Swal.fire("Error", "Zone Code is required", "error");
      return false;
    }
    if (!zone.zoneName?.trim()) {
      Swal.fire("Error", "Zone Name is required", "error");
      return false;
    }
    if (!zone.countryCode) {
      Swal.fire("Error", "Country is required", "error");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const createdBy = currentUser?.userIdCode || "SYSADMIN001";
      let response;

      if (isEditMode) {
        response = await zoneMasterAPI.update(zoneCode, zone, createdBy);
      } else {
        response = await zoneMasterAPI.create(zone, createdBy);
      }

      if (response && response.success) {
        Swal.fire({
          title: "Success",
          text: response.message || "Zone saved successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/zones");
        });
      } else {
        Swal.fire("Error", response.message || "Failed to save zone", "error");
      }
    } catch (e) {
      console.error("Error saving zone:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message || "Failed to save zone. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="add-edit-product-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-product-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-product-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/zones"
            onClick={(e) => {
              e.preventDefault();
              navigate("/zones");
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            Zone Master
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isEditMode ? "Edit Zone" : "Add Zone"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-product-header-right">
          <Button
            className="add-edit-product-back-button"
            onClick={() => navigate("/zones")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-product-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <div className="container-fluid add-edit-product-page">
        {/* Basic Information Section */}
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-lg-4" style={{ display: "none" }}>
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Zone Code <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={zone.zoneCode}
                  required
                  fullWidth
                  disabled={true}
                  size="small"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Zone ID
                </label>
                <TextField
                  value={zone.zoneId}
                  onChange={(e) => handleChange("zoneId", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter zone ID"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  Country <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={countrySelectRef}
                  className="form-control select2-country"
                  required
                >
                  <option value="">Select Country</option>
                  {dropdowns.countries.map((c) => (
                    <option key={c.countryCode} value={c.countryCode}>
                      {c.countryName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Zone Name <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={zone.zoneName}
                  onChange={(e) => handleChange("zoneName", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter zone name"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate("/zones")}
                className="btn-cancel-product funbtns"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !canSave}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                className="btn-save-product funbtns"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AddEditZoneMaster;

